import React, { useState, useRef, useSyncExternalStore, useEffect } from 'react';
import { useMutation } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import {
  assetsClipAssetInternalDebuggerCreate,
  type AssetsClipAssetInternalDebuggerCreateProps
} from '@goldcast/api/content';
import appLogger from '@/services/logger';
import Button from '@/components/atoms/Button/Button';
import Icon from '@/components/atoms/Icon';
import ContentDialog from '@/Pages/Sessions/uiComponents/ContentDialog/ContentDialog';
import HlsPlayerV2 from '@/Pages/Clip/ClipPlayer/HlsPlayerV2';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';
import ErrorContainer from '@/App/ErrorBoundary/ErrorContainer';
import { currentUser } from '@/stores/user';

interface SearchResult {
  title: string;
  content_id: string;
  project_id: string;
  metadata: {
    section_transcript: string;
    cosine_distance: number;
    start_time: number;
    end_time: number;
    description: string;
    takeaway: string;
  };
  hls_url: string;
}

interface ApiResponse {
  ai_search: {
    existing: {
      stage_1: SearchResult[];
      stage_2: SearchResult[];
      stage_3: SearchResult[];
    };
    enriched: {
      stage_1: SearchResult[];
      stage_2: SearchResult[];
      stage_3: SearchResult[];
    };
  };
}

export default function AiSearchInternalPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const organization = currentUser.getSnapshot()?.organization;

  const [searchQuery, setSearchQuery] = useState('');
  const [organizationId, setOrganizationId] = useState(organization || '');
  const [llm, setLlm] = useState('claude_3_7_sonnet');
  const [queryUnderstandingPromptVersion, setQueryUnderstandingPromptVersion] = useState('2');
  const [useHybridEmbeddings, setUseHybridEmbeddings] = useState(true);
  const [searchResults, setSearchResults] = useState<ApiResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedResult, setSelectedResult] = useState<SearchResult | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [hasAttemptedAutoplay, setHasAttemptedAutoplay] = useState(false);
  const [expandedResults, setExpandedResults] = useState<Set<string>>(new Set());
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const searchQueryFromURL = searchParams.get('q');
    const organizationIdFromURL = searchParams.get('org');
    const llmFromURL = searchParams.get('llm');
    const promptFromURL = searchParams.get('prompt');
    const hybridEmbeddingsFromURL = searchParams.get('hybrid');

    if (searchQueryFromURL) {
      setSearchQuery(searchQueryFromURL);
    }
    if (organizationIdFromURL) {
      setOrganizationId(organizationIdFromURL);
    }
    if (llmFromURL) {
      setLlm(llmFromURL);
    }
    if (promptFromURL) {
      setQueryUnderstandingPromptVersion(promptFromURL);
    }
    if (hybridEmbeddingsFromURL) {
      setUseHybridEmbeddings(hybridEmbeddingsFromURL === 'true');
    }
  }, [searchParams]);

  const { mutate: performSearch, isLoading } = useMutation(
    async (params: AssetsClipAssetInternalDebuggerCreateProps) => {
      const response = await assetsClipAssetInternalDebuggerCreate(params);
      return response as unknown as ApiResponse;
    },
    {
      onSuccess: (data: ApiResponse) => {
        setSearchResults(data);
        setError(null);
        setExpandedResults(new Set()); // Reset expanded state on new search
      },
      onError: (error: any) => {
        appLogger.error('Search error', error);
        setError('Failed to fetch search results. Please try again.');
      }
    }
  );

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const aiSearchInternalEnabled = featureFlags[FeatureFlagKeys.Use_CL_AI_Debug_Tools];

  // Show 404 page if feature flag is disabled
  if (!aiSearchInternalEnabled) {
    return (
      <ErrorContainer
        errorMessage="Page not found. If the issue persists, contact our support team and they will be happy to help."
        errorDisplayCode="404"
      />
    );
  }

  const handleSearch = () => {
    if (!searchQuery.trim() || !organizationId.trim()) {
      setError('Please fill in both search query and organization ID');
      return;
    }

    setError(null);

    // Update URL with search parameters only when search is clicked
    const newSearchParams = new URLSearchParams();
    if (searchQuery.trim()) {
      newSearchParams.set('q', searchQuery.trim());
    }
    if (organizationId.trim()) {
      newSearchParams.set('org', organizationId.trim());
    }
    if (llm) {
      newSearchParams.set('llm', llm);
    }
    if (queryUnderstandingPromptVersion) {
      newSearchParams.set('prompt', queryUnderstandingPromptVersion);
    }
    if (useHybridEmbeddings) {
      newSearchParams.set('hybrid', 'true');
    }

    setSearchParams(newSearchParams, { replace: true });

    const mutationParams: AssetsClipAssetInternalDebuggerCreateProps = {
      body: {
        action: 'ai_search' as any, // Using 'as any' since ai_search is not in the enum yet
        organisation_id: organizationId,
        search_term: searchQuery,
        llm: llm,
        query_understanding_prompt_version: queryUnderstandingPromptVersion,
        use_hybrid_embeddings: useHybridEmbeddings
      } as any
    };

    performSearch(mutationParams);
  };

  // Reset form and clear URL parameters
  const handleReset = () => {
    setSearchQuery('');
    setOrganizationId('');
    setLlm('claude_3_7_sonnet');
    setQueryUnderstandingPromptVersion('2');
    setUseHybridEmbeddings(false);
    setSearchResults(null);
    setError(null);
    setExpandedResults(new Set());

    // Clear URL parameters using React Router
    setSearchParams({}, { replace: true });
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleTitleClick = (result: SearchResult) => {
    setSelectedResult(result);
    setIsDialogOpen(true);
    setHasAttemptedAutoplay(false);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setSelectedResult(null);
    setHasAttemptedAutoplay(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const handleVideoLoaded = async () => {
    if (videoRef.current && selectedResult && !hasAttemptedAutoplay) {
      videoRef.current.currentTime = selectedResult.metadata.start_time;

      await videoRef.current.play();

      setHasAttemptedAutoplay(true);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current && selectedResult) {
      const currentTime = videoRef.current.currentTime;
      setCurrentVideoTime(currentTime);
      if (currentTime >= selectedResult.metadata.end_time) {
        videoRef.current.pause();
      }
    }
  };

  const handleVideoError = () => {
    if (videoRef.current) {
      const error = videoRef.current.error;
      if (error) {
        appLogger.error('Video error', { errorCode: error.code, errorMessage: error.message });
      }
    }
  };

  const toggleExpanded = (resultId: string) => {
    setExpandedResults(prev => {
      const newSet = new Set(prev);
      if (newSet.has(resultId)) {
        newSet.delete(resultId);
      } else {
        newSet.add(resultId);
      }
      return newSet;
    });
  };

  const renderSearchResults = (results: SearchResult[] | undefined | null, title: string, colorClass: string) => {
    if (!results || results.length === 0) {
      return (
        <div className="flex-1 p-4">
          <h3 className="mb-4 text-center text-lg font-semibold">{title}</h3>
          <div className="text-center text-gray-500">No results found</div>
        </div>
      );
    }

    return (
      <div className="flex-1 p-4">
        <h3 className="mb-4 text-center text-lg font-semibold">{title}</h3>
        <div className="space-y-3">
          {results.map((result, index) => {
            const resultId = `${result.content_id}-${index}`;
            const isExpanded = expandedResults.has(resultId);

            return (
              <div
                key={resultId}
                className={`group relative overflow-hidden rounded-xl border-2 p-4 ${colorClass} transition-all duration-300 hover:scale-[1.01] hover:shadow-lg`}
              >
                {/* Header with Title and Play Button */}
                <div className="mb-3 flex items-start justify-between">
                  <div
                    className="flex-1 cursor-pointer text-sm font-semibold transition-colors duration-200 hover:text-blue-600"
                    onClick={() => handleTitleClick(result)}
                    title="Click to view clip"
                  >
                    {result.title}
                  </div>

                  {/* Play Button */}
                  <button
                    onClick={() => handleTitleClick(result)}
                    className="group relative ml-3 overflow-hidden rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 px-4 py-2 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:from-blue-500 hover:to-indigo-600 hover:shadow-xl active:scale-95"
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      <Icon
                        name="IconVideo"
                        className="size-4 transition-transform duration-200 group-hover:scale-110"
                      />
                    </span>
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
                  </button>
                </div>

                {/* Basic Info - Always Visible */}
                <div className="mb-3 space-y-2 text-xs text-gray-600">
                  <div className="flex justify-between">
                    <div>
                      <div className="mb-1 font-bold text-gray-700">Time</div>
                      <div className="text-gray-800">
                        {formatTime(result.metadata.start_time)} - {formatTime(result.metadata.end_time)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Show More/Less Link */}
                <div className="flex justify-center">
                  <button
                    onClick={() => toggleExpanded(resultId)}
                    className="flex items-center gap-1.5 text-sm font-medium text-blue-400 underline-offset-2 transition-colors duration-200 hover:text-indigo-500 hover:underline"
                  >
                    {isExpanded ? (
                      <>
                        <Icon name="IconChevronUp" className="size-3" />
                        Show Less
                      </>
                    ) : (
                      <>
                        <Icon name="IconChevronDown" className="size-3" />
                        Show More
                      </>
                    )}
                  </button>
                </div>

                {/* Expanded Content - Only visible when expanded */}
                {isExpanded && (
                  <div className="mt-4 space-y-2 border-t border-gray-200 pt-4 text-xs text-gray-600">
                    <div>
                      <div className="mb-1 font-bold text-gray-700">Content ID</div>
                      <div className="text-gray-800">{result.content_id}</div>
                    </div>
                    <div>
                      <div className="mb-1 font-bold text-gray-700">Project ID</div>
                      <div className="text-gray-800">{result.project_id}</div>
                    </div>
                    <div>
                      <div className="mb-1 font-bold text-gray-700">Cosine Distance</div>
                      <div className="text-gray-800">{result.metadata.cosine_distance}</div>
                    </div>
                    <div>
                      <div className="mb-1 font-bold text-gray-700">Description</div>
                      <div className="text-gray-800">{result.metadata.description}</div>
                    </div>
                    <div>
                      <div className="mb-1 font-bold text-gray-700">Section Transcript</div>
                      <div className="text-gray-800">{result.metadata.section_transcript}</div>
                    </div>
                    <div>
                      <div className="mb-1 font-bold text-gray-700">Takeaway</div>
                      <div className="text-gray-800">{result.metadata.takeaway}</div>
                    </div>
                    <div>
                      <div className="mb-1 font-bold text-gray-700">HLS URL</div>
                      <div className="break-all text-gray-800">{result.hls_url}</div>
                    </div>

                    {/* Dynamically render any additional metadata fields */}
                    {Object.entries(result.metadata).map(([key, value]) => {
                      // Skip already displayed fields
                      const displayedFields = [
                        'start_time',
                        'end_time',
                        'cosine_distance',
                        'description',
                        'section_transcript',
                        'takeaway'
                      ];
                      if (displayedFields.includes(key)) {
                        return null;
                      }

                      // Skip if value is null, undefined, or empty string
                      if (value === null || value === undefined || value === '') {
                        return null;
                      }

                      // Format the key for display (convert snake_case to Title Case)
                      const displayKey = key
                        .split('_')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ');

                      return (
                        <div key={key}>
                          <div className="mb-1 font-bold text-gray-700">{displayKey}</div>
                          <div className="text-gray-800">
                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="mx-auto max-w-6xl px-4 py-8">
        {/* Header Section */}
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-3xl font-bold text-gray-900">AI Search Internal POC</h1>
          <p className="text-gray-600">Compare existing vs enhanced AI search results</p>
        </div>

        {/* Search Input Section */}
        <div className="mb-8 rounded-xl bg-white p-8 shadow-lg">
          <div className="mb-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-800">Search Parameters</h2>
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">User Search Query</label>
                  <input
                    className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-deep-orange ring-2 ring-transparent transition-all duration-150 ease-in-out focus:border-transparent focus:outline-none focus:ring-2 focus:ring-theme-input-focus"
                    placeholder="Enter your search query..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Organization ID (UUID)</label>
                  <input
                    className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-deep-orange ring-2 ring-transparent transition-all duration-150 ease-in-out focus:border-transparent focus:outline-none focus:ring-2 focus:ring-theme-input-focus"
                    placeholder="Enter organization ID..."
                    value={organizationId}
                    onChange={e => setOrganizationId(e.target.value)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">LLM Model</label>
                  <input
                    className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-deep-orange ring-2 ring-transparent transition-all duration-150 ease-in-out focus:border-transparent focus:outline-none focus:ring-2 focus:ring-theme-input-focus"
                    placeholder="Enter LLM model..."
                    value={llm}
                    onChange={e => setLlm(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Query Understanding Prompt Version</label>
                  <input
                    className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-deep-orange ring-2 ring-transparent transition-all duration-150 ease-in-out focus:border-transparent focus:outline-none focus:ring-2 focus:ring-theme-input-focus"
                    placeholder="Enter prompt version..."
                    value={queryUnderstandingPromptVersion}
                    onChange={e => setQueryUnderstandingPromptVersion(e.target.value)}
                  />
                </div>
              </div>

              {/* Hybrid Embeddings Checkbox */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="useHybridEmbeddings"
                  checked={useHybridEmbeddings}
                  onChange={e => setUseHybridEmbeddings(e.target.checked)}
                  className="size-4 rounded border-gray-300 text-deep-orange focus:ring-2 focus:ring-theme-input-focus focus:ring-offset-2"
                />
                <label htmlFor="useHybridEmbeddings" className="text-sm font-medium text-gray-700">
                  Use Hybrid Embeddings
                </label>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center space-x-4">
            <Button
              variation="filled"
              buttonSize="large"
              onClick={handleSearch}
              disabled={isLoading}
              trackingId="ai-search-button"
              className="min-w-[120px]"
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <Icon name="IconLoader2" className="size-4 animate-spin text-white" />
                  <span>Searching...</span>
                </div>
              ) : (
                'Search'
              )}
            </Button>

            <Button
              variation="outline"
              buttonSize="large"
              onClick={handleReset}
              disabled={isLoading}
              trackingId="ai-search-reset-button"
              className="min-w-[120px]"
            >
              Reset
            </Button>
          </div>

          {error && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
              <div className="flex items-center">
                <Icon name="IconAlertCircle" className="mr-2 size-4" />
                {error}
              </div>
            </div>
          )}
        </div>

        {/* Results Section */}
        {searchResults && !isLoading && searchResults?.ai_search && (
          <div className="rounded-xl bg-white shadow-lg">
            {/* Unified Results View - All stages and approaches side by side */}
            {(() => {
              const stage3Existing = searchResults.ai_search.existing?.stage_3 || [];
              const stage2Existing = searchResults.ai_search.existing?.stage_2 || [];
              const stage1Existing = searchResults.ai_search.existing?.stage_1 || [];
              const stage3Enriched = searchResults.ai_search.enriched?.stage_3 || [];
              const stage2Enriched = searchResults.ai_search.enriched?.stage_2 || [];
              const stage1Enriched = searchResults.ai_search.enriched?.stage_1 || [];

              if (
                stage3Existing.length === 0 &&
                stage2Existing.length === 0 &&
                stage1Existing.length === 0 &&
                stage3Enriched.length === 0 &&
                stage2Enriched.length === 0 &&
                stage1Enriched.length === 0
              ) {
                return null;
              }

              return (
                <div className="bg-white">
                  <div className="border-b border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-800">Unified Results Comparison</h3>
                    <p className="mt-1 text-sm text-gray-600">Compare all stages and approaches in one view</p>
                  </div>

                  {/* Results Grid - Dynamic columns based on available stages */}
                  <div
                    className={`grid grid-cols-1 gap-0 ${
                      [
                        stage3Existing.length > 0 || stage3Enriched.length > 0,
                        stage2Existing.length > 0 || stage2Enriched.length > 0,
                        stage1Existing.length > 0 || stage1Enriched.length > 0
                      ].filter(Boolean).length === 1
                        ? 'xl:grid-cols-1'
                        : [
                              stage3Existing.length > 0 || stage3Enriched.length > 0,
                              stage2Existing.length > 0 || stage2Enriched.length > 0,
                              stage1Existing.length > 0 || stage1Enriched.length > 0
                            ].filter(Boolean).length === 2
                          ? 'xl:grid-cols-2'
                          : 'xl:grid-cols-3'
                    }`}
                  >
                    {/* Stage 3 Column - Only show if either Existing or Enhanced has results */}
                    {(stage3Existing.length > 0 || stage3Enriched.length > 0) && (
                      <div className="border-r border-gray-200">
                        <div className="border-b border-gray-200 bg-blue-50 p-4">
                          <h4 className="text-center text-sm font-semibold text-blue-800">Stage 3</h4>
                        </div>
                        {/* Enhanced AI Search - Stage 3 */}
                        <div className="border-b border-gray-100 p-3">
                          <div className="mb-2">
                            <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                              Enhanced
                            </span>
                            <span className="ml-2 text-xs text-gray-500">({stage3Enriched.length})</span>
                          </div>
                          {renderSearchResults(
                            stage3Enriched,
                            '',
                            'bg-green-50 border-green-200 text-green-800 hover:bg-green-100'
                          )}
                        </div>

                        {/* Existing AI Search - Stage 3 */}
                        <div className="p-3">
                          <div className="mb-2">
                            <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800">
                              Existing
                            </span>
                            <span className="ml-2 text-xs text-gray-500">({stage3Existing.length})</span>
                          </div>
                          {renderSearchResults(
                            stage3Existing,
                            '',
                            'bg-red-50 border-red-200 text-red-800 hover:bg-red-100'
                          )}
                        </div>
                      </div>
                    )}

                    {/* Stage 2 Column - Only show if either Existing or Enhanced has results */}
                    {(stage2Existing.length > 0 || stage2Enriched.length > 0) && (
                      <div className="border-r border-gray-200">
                        <div className="border-b border-gray-200 bg-purple-50 p-4">
                          <h4 className="text-center text-sm font-semibold text-purple-800">Stage 2</h4>
                        </div>

                        {/* Enhanced AI Search - Stage 2 */}
                        <div className="border-b border-gray-100 p-3">
                          <div className="mb-2">
                            <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                              Enhanced
                            </span>
                            <span className="ml-2 text-xs text-gray-500">({stage2Enriched.length})</span>
                          </div>
                          {renderSearchResults(
                            stage2Enriched,
                            '',
                            'bg-green-50 border-green-200 text-green-800 hover:bg-green-100'
                          )}
                        </div>

                        {/* Existing AI Search - Stage 2 */}
                        <div className="p-3">
                          <div className="mb-2">
                            <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800">
                              Existing
                            </span>
                            <span className="ml-2 text-xs text-gray-500">({stage2Existing.length})</span>
                          </div>
                          {renderSearchResults(
                            stage2Existing,
                            '',
                            'bg-red-50 border-red-200 text-red-800 hover:bg-red-100'
                          )}
                        </div>
                      </div>
                    )}

                    {/* Stage 1 Column - Only show if either Existing or Enhanced has results */}
                    {(stage1Existing.length > 0 || stage1Enriched.length > 0) && (
                      <div className="border-r border-gray-200">
                        <div className="border-b border-gray-200 bg-gray-50 p-4">
                          <h4 className="text-center text-sm font-semibold text-gray-800">Stage 1</h4>
                        </div>

                        {/* Enhanced AI Search - Stage 1 */}
                        <div className="border-b border-gray-100 p-3">
                          <div className="mb-2">
                            <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                              Enhanced
                            </span>
                            <span className="ml-2 text-xs text-gray-500">({stage1Enriched.length})</span>
                          </div>
                          {renderSearchResults(
                            stage1Enriched,
                            '',
                            'bg-green-50 border-green-200 text-green-800 hover:bg-green-100'
                          )}
                        </div>

                        {/* Existing AI Search - Stage 1 */}
                        <div className="border-gray-100 p-3">
                          <div className="mb-2">
                            <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800">
                              Existing
                            </span>
                            <span className="ml-2 text-xs text-gray-500">({stage1Existing.length})</span>
                          </div>
                          {renderSearchResults(
                            stage1Existing,
                            '',
                            'bg-red-50 border-red-200 text-red-800 hover:bg-red-100'
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Empty State */}
        {!searchResults && !isLoading && (
          <div className="rounded-xl bg-white p-12 text-center shadow-lg">
            <div className="mx-auto max-w-md">
              <Icon name="IconSearch" className="mx-auto size-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">No Search Results</h3>
              <p className="mt-2 text-sm text-gray-500">Enter your search parameters and click Search to see results</p>
            </div>
          </div>
        )}

        {/* Video Player Dialog */}
        <ContentDialog
          isOpen={isDialogOpen}
          setIsOpen={closeDialog}
          title={selectedResult?.title || ''}
          size="large"
          panelClassNames="!max-w-5xl"
        >
          {selectedResult && (
            <div className="p-8">
              {/* Video Player Section */}
              <div className="mb-8">
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-900 shadow-lg">
                  <HlsPlayerV2
                    url={selectedResult.hls_url}
                    videoRef={videoRef}
                    onLoaded={handleVideoLoaded}
                    onError={handleVideoError}
                    videoProps={{
                      className: 'w-full h-[500px] rounded-xl object-contain',
                      onTimeUpdate: handleTimeUpdate
                    }}
                  />
                </div>
              </div>

              {/* Metadata Section */}
              <div className="grid gap-6 md:grid-cols-2">
                {/* Time Information */}
                <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                  <div className="mb-4 flex items-center gap-2">
                    <div className="rounded-full bg-blue-100 p-2">
                      <Icon name="IconClock" className="size-5 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800">Time Information</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
                      <span className="font-medium text-gray-700">Start Time</span>
                      <span className="font-mono text-sm text-gray-900">
                        {formatTime(selectedResult.metadata.start_time)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
                      <span className="font-medium text-gray-700">End Time</span>
                      <span className="font-mono text-sm text-gray-900">
                        {formatTime(selectedResult.metadata.end_time)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between rounded-lg bg-green-50 p-3">
                      <span className="font-medium text-green-700">Current Time</span>
                      <span className="font-mono text-sm font-semibold text-green-900">
                        {formatTime(currentVideoTime)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Clip Details */}
                {selectedResult.metadata.description && (
                  <div className="mt-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                    <div className="mb-4 flex items-center gap-2">
                      <div className="rounded-full bg-purple-100 p-2">
                        <Icon name="IconFileText" className="size-5 text-purple-600" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800">Description</h3>
                    </div>
                    <p className="rounded-lg bg-gray-50 p-4 leading-relaxed text-gray-700">
                      {selectedResult.metadata.description}
                    </p>
                  </div>
                )}
              </div>

              {/* Description Section */}
            </div>
          )}
        </ContentDialog>
      </div>
    </div>
  );
}
